<script setup lang="ts">
import BooleanIcon from '@/components/shared/BooleanIcon.vue';
import DeleteConfirmationDialog from '@/components/DeleteConfirmationDialog.vue';
import ButtonContent from '@/components/devextreme/ButtonContent.vue';
import StationEditPopup from '@/components/maintenance/StationEditPopup.vue';
import { useMaintenanceDelete } from '@/composables/useMaintenanceDelete';
import { RoleGroups } from '@/constants/Roles';
import { removedMessage } from '@/helpers/notificationMessages';
import { t } from '@/i18n';
import { dataSource, useStationService } from '@/services/api/stationService';
import type { IStationResponse } from '@/types/interfaces/MaintenanceTypes';
import Icons from '@/constants/Icons';
import DxButton from 'devextreme-vue/button';
import { DxColumn, DxDataGrid, DxToolbar, DxItem } from 'devextreme-vue/data-grid';
import { ref, shallowRef } from 'vue';

definePage({
  name: 'MaintenanceStations',
  meta: {
    title: 'maintenance.tabs.stations',
    roles: RoleGroups.Maintenance
  }
});

const stationsGridRef = shallowRef<DxDataGrid>();
const stationEditPopupRef = ref<typeof StationEditPopup>();
const deleteDialogRef = ref<InstanceType<typeof DeleteConfirmationDialog>>();
const api = useStationService();

async function confirmDelete(data: IStationResponse): Promise<void> {
  await deleteDialogRef?.value?.show(data.code, `${data.code} - ${data.name}`);
}

const deleteStation = useMaintenanceDelete({
  dataSource,
  dialog: () => deleteDialogRef.value,
  remove: (stationCode: string) => api.deleteStation(stationCode),
  key: stationCode => stationCode,
  removedMessage: stationCode => removedMessage(t('entities.station'), stationCode.trim().toUpperCase())
});

async function updateData(data: IStationResponse, key?: string | null): Promise<void> {
  dataSource.upsert(data, key);
}
</script>

<template>
  <StationEditPopup ref="stationEditPopupRef" @update="updateData" />
  <DeleteConfirmationDialog ref="deleteDialogRef" :entity="$t('entities.station')"
      @confirm="deleteStation" />

  <DxDataGrid id="stationsGrid" ref="stationsGridRef" :data-source="dataSource">
    <DxToolbar>
      <DxItem location="before" template="title-template" />
      <DxItem location="before" template="add-button-template" />
      <DxItem location="after" template="refresh-button-template" />
      <DxItem :locate-in-menu="'auto'" location="after" name="searchPanel" />
    </DxToolbar>

    <DxColumn :sort-index="0" :sort-order="'asc'" data-field="code" width="150px" />
    <DxColumn :hiding-priority="5" data-field="name" />
    <DxColumn :hiding-priority="3" data-field="city" width="250px" />
    <DxColumn :hiding-priority="2" :caption="$t('labels.country')" data-field="countryCode" width="150px" />
    <DxColumn :hiding-priority="0" :caption="$t('labels.timeZone')" data-field="timeZoneId" width="250px" />
    <DxColumn :hiding-priority="1" data-field="olsonZone" width="250px" />
    <DxColumn :hiding-priority="4" cell-template="boolean-cell-template" data-field="enabled"
        :false-text="$t('common.no')" :true-text="$t('common.yes')" width="150px" />
    <DxColumn :visible="false" data-field="airsideEnabled" />
    <DxColumn :visible="false" type="adaptive" />
    <DxColumn :alignment="'center'" :allow-filtering="false" :allow-sorting="false" caption="" cell-template="command-cell-template"
        data-field="code" name="command-column" width="100px" />

    <template #title-template>
      <span class="h3 fw-semibold">{{ $t('maintenance.maintain', { entities: $t('maintenance.tabs.stations') }) }}</span>
    </template>

    <template #add-button-template>
      <DxButton class="ms-3" type="success" @click="stationEditPopupRef?.addStation()">
        <ButtonContent :icon="Icons.Plus" :text="$t('common.addEntity', { entity: $t('entities.station') })" />
      </DxButton>
    </template>

    <template #refresh-button-template>
      <DxButton type="primary" @click="stationsGridRef?.instance?.refresh()">
        <ButtonContent :icon="Icons.ArrowsRotate" />
      </DxButton>
    </template>

    <template #boolean-cell-template="{ data }">
      <BooleanIcon :value="data.value" />
    </template>

    <template #command-cell-template="{ data }">
      <DxButton type="primary" @click="stationEditPopupRef?.editStation(data.value)">
        <ButtonContent :icon="Icons.Pencil" />
      </DxButton>
      <DxButton :visible="!data.data.airsideEnabled" type="danger" @click="confirmDelete(data.data)">
        <ButtonContent :icon="Icons.Trash" />
      </DxButton>
    </template>
  </DxDataGrid>
</template>

<style scoped>

</style>